import { problemResponse, registry, undeclaredQueryRefused } from "../registry.js";
import { z } from "../zod.js";
import {
  EntitlementOverrideKind,
  PlatformOverrideRemoveRequest,
  PlatformOverrideSetRequest,
  PlatformPlanCreateRequest,
  PlatformPlanListResponse,
  PlatformPlanUpdateRequest,
  PlatformPlanView,
  PlatformSubdomainRenameRequest,
  PlatformTenantEntitlementsResponse,
  PlatformTenantUpdateRequest,
  PlatformTenantUsageResponse,
} from "./entitlement-schemas.js";
import { PlatformTenantDetail } from "./schemas.js";

// /v1/platform/plans*, the tenant edit routes, a tenant's entitlements and
// overrides, and its usage (M15-6 and M15-7a; spec 15 §5). Admin host only,
// behind the staff cookie, like every /v1/platform/* route. Mutations take an
// Idempotency-Key against the staff-keyed store.

const staffSecurity = [{ platformSessionCookie: [] }];
const notAdminHost = problemResponse("Requested on a host other than the admin host");
const staffForbidden = (what: string) =>
  problemResponse(`${what}, or TOTP_ENROLLMENT_REQUIRED while the caller's TOTP is unconfirmed`);
const mintedKey = problemResponse(
  "VALIDATION_FAILED: the Idempotency-Key is in a namespace the server mints for itself (D-340 ruling 4)",
);
const keyReuse = problemResponse("IDEMPOTENCY_KEY_REUSE or IDEMPOTENCY_IN_PROGRESS");
const IdParam = z.object({ id: z.guid() });

registry.register("PlatformPlanView", PlatformPlanView);

// --- plans (spec 15 §2.7 to §2.9, S12) ---

registry.registerPath({
  method: "get",
  path: "/v1/platform/plans",
  operationId: "platformListPlans",
  tags: ["platform"],
  summary: "List plans with their tenant counts (all staff roles)",
  description:
    "Retired plans are left out unless include_retired=true (D-486 ruling 4). annual_price_minor is null for platform_support and platform_readonly (spec 15 §7, D-486 ruling 3).",
  security: staffSecurity,
  request: {
    query: z.object({ include_retired: z.enum(["true", "false"]).optional() }),
  },
  responses: {
    200: {
      description: "Plans, active first, then by name",
      content: { "application/json": { schema: PlatformPlanListResponse } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Not authenticated"),
    403: staffForbidden("Not permitted"),
    404: notAdminHost,
  },
});

registry.registerPath({
  method: "get",
  path: "/v1/platform/plans/{id}",
  operationId: "platformGetPlan",
  tags: ["platform"],
  summary: "One plan (all staff roles)",
  security: staffSecurity,
  request: { params: IdParam },
  responses: {
    200: {
      description: "The plan",
      content: { "application/json": { schema: PlatformPlanView } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Not authenticated"),
    403: staffForbidden("Not permitted"),
    404: problemResponse("PLAN_NOT_FOUND"),
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/platform/plans",
  operationId: "platformCreatePlan",
  tags: ["platform"],
  summary: "Create a plan (platform_admin)",
  description:
    "Each limit takes its code's canonical enforcement (spec 15 §2.9); storage_gb needs warn_pct and block_pct with block above warn, and no other code takes them. A sandbox admin creates sandbox plans only (D-615 ruling 3). Audited plan.create.",
  security: staffSecurity,
  request: { body: { content: { "application/json": { schema: PlatformPlanCreateRequest } } } },
  responses: {
    201: {
      description: "The plan as created",
      content: { "application/json": { schema: PlatformPlanView } },
    },
    400: problemResponse("Validation failed, including a module or limit code given twice"),
    401: problemResponse("Not authenticated"),
    403: problemResponse("Not permitted"),
    404: notAdminHost,
    409: problemResponse("PLAN_CODE_TAKEN, IDEMPOTENCY_KEY_REUSE or IDEMPOTENCY_IN_PROGRESS"),
    422: problemResponse(
      "MODULE_CODE_UNKNOWN, LIMIT_THRESHOLDS_INVALID, or VALIDATION_FAILED for a minted Idempotency-Key (D-340 ruling 4)",
    ),
  },
});

registry.registerPath({
  method: "patch",
  path: "/v1/platform/plans/{id}",
  operationId: "platformUpdatePlan",
  tags: ["platform"],
  summary: "Edit a plan (platform_admin)",
  description:
    "Every tenant on the plan sees the change within the entitlement cache's poll (D-487 ruling 1). A sandbox admin edits sandbox plans only (403 STAFF_SCOPE_DENIED). A retired plan is not edited (409 PLAN_RETIRED). Audited plan.update with before and after.",
  security: staffSecurity,
  request: {
    params: IdParam,
    body: { content: { "application/json": { schema: PlatformPlanUpdateRequest } } },
  },
  responses: {
    200: {
      description: "The plan as edited",
      content: { "application/json": { schema: PlatformPlanView } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Not authenticated"),
    403: problemResponse("Not permitted, or STAFF_SCOPE_DENIED (D-615 ruling 3)"),
    404: problemResponse("PLAN_NOT_FOUND"),
    409: problemResponse("PLAN_RETIRED, IDEMPOTENCY_KEY_REUSE or IDEMPOTENCY_IN_PROGRESS"),
    422: problemResponse("MODULE_CODE_UNKNOWN, LIMIT_THRESHOLDS_INVALID, or a minted key"),
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/platform/plans/{id}/retire",
  operationId: "platformRetirePlan",
  tags: ["platform"],
  summary: "Retire a plan (platform_admin)",
  description:
    "New assignment is refused from now on; tenants already on it keep it (spec 15 §2.7). Retiring a retired plan answers the plan unchanged. Pilot, the plan every unassigned tenant is on and a create without plan_id is given, is never retired (409 PILOT_PLAN_REQUIRED). Audited plan.retire.",
  security: staffSecurity,
  request: { params: IdParam },
  responses: {
    200: {
      description: "The plan, retired",
      content: { "application/json": { schema: PlatformPlanView } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Not authenticated"),
    403: problemResponse("Not permitted, or STAFF_SCOPE_DENIED (D-615 ruling 3)"),
    404: problemResponse("PLAN_NOT_FOUND"),
    409: problemResponse("PILOT_PLAN_REQUIRED, IDEMPOTENCY_KEY_REUSE or IDEMPOTENCY_IN_PROGRESS"),
    422: mintedKey,
  },
});

// --- tenant edits (spec 15 §3.3) ---

registry.registerPath({
  method: "patch",
  path: "/v1/platform/tenants/{id}",
  operationId: "platformUpdateTenant",
  tags: ["platform"],
  summary: "Rename a tenant, change its defaults or move it to another plan (platform_admin)",
  description:
    "Name and defaults change through module 16's TenancyService. A plan move takes effect at once and is audited with the effective entitlements before and after; a plan whose max_facilities is below the tenant's active facility count answers 409 LIMIT_BELOW_USAGE unless force=true (existing facilities stay). A retired plan answers 409 PLAN_RETIRED, a sandbox plan on a fleet tenant 409 PLAN_SANDBOX_ONLY. The currency is never changed here (spec 16 §2.1). Sandbox staff edit sandbox tenants only.",
  security: staffSecurity,
  request: {
    params: IdParam,
    body: { content: { "application/json": { schema: PlatformTenantUpdateRequest } } },
  },
  responses: {
    200: {
      description: "The tenant as edited",
      content: { "application/json": { schema: PlatformTenantDetail } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Not authenticated"),
    403: problemResponse("Not permitted, or STAFF_SCOPE_DENIED (D-477)"),
    404: problemResponse("Unknown tenant"),
    409: problemResponse(
      "LIMIT_BELOW_USAGE, PLAN_RETIRED, PLAN_SANDBOX_ONLY, INVALID_TENANT_STATUS, IDEMPOTENCY_KEY_REUSE or IDEMPOTENCY_IN_PROGRESS",
    ),
    422: problemResponse("PLAN_UNKNOWN, PACK_NOT_IN_PLAN, or a minted key"),
  },
});

registry.registerPath({
  method: "post",
  path: "/v1/platform/tenants/{id}/subdomain",
  operationId: "platformRenameTenantSubdomain",
  tags: ["platform"],
  summary: "Move a tenant to a new subdomain (platform_admin)",
  description:
    "TenancyService.renameSubdomain: the same checks as create, and the old name redirects for 30 days (spec 16 §2.2). Audited tenant.subdomain_renamed with the reason.",
  security: staffSecurity,
  request: {
    params: IdParam,
    body: { content: { "application/json": { schema: PlatformSubdomainRenameRequest } } },
  },
  responses: {
    200: {
      description: "The tenant at its new subdomain",
      content: { "application/json": { schema: PlatformTenantDetail } },
    },
    400: problemResponse("Validation failed or SUBDOMAIN_INVALID"),
    401: problemResponse("Not authenticated"),
    403: problemResponse("Not permitted, or STAFF_SCOPE_DENIED (D-477)"),
    404: problemResponse("Unknown tenant"),
    409: problemResponse(
      "SUBDOMAIN_TAKEN, SUBDOMAIN_RESERVED, IDEMPOTENCY_KEY_REUSE or IDEMPOTENCY_IN_PROGRESS",
    ),
    422: mintedKey,
  },
});

// --- entitlements and overrides (spec 15 §2.10, §3.4, S6) ---

registry.registerPath({
  method: "get",
  path: "/v1/platform/tenants/{id}/entitlements",
  operationId: "platformTenantEntitlements",
  tags: ["platform"],
  summary: "A tenant's effective entitlements: plan, override and effective value per code",
  security: staffSecurity,
  request: { params: IdParam },
  responses: {
    200: {
      description: "The effective matrix",
      content: { "application/json": { schema: PlatformTenantEntitlementsResponse } },
    },
    400: undeclaredQueryRefused,
    401: problemResponse("Not authenticated"),
    403: staffForbidden("Not permitted"),
    404: problemResponse("Unknown tenant"),
  },
});

const OverrideParams = z.object({
  id: z.guid(),
  kind: EntitlementOverrideKind,
  code: z.string().min(1).max(80),
});

registry.registerPath({
  method: "put",
  path: "/v1/platform/tenants/{id}/overrides/{kind}/{code}",
  operationId: "platformSetEntitlementOverride",
  tags: ["platform"],
  summary: "Set a per-tenant override (platform_admin)",
  description:
    "A module override takes enabled, a limit override takes limit_value; the reason is required and an expiry, when given, is in the future (422 OVERRIDE_INVALID otherwise). Audited entitlement.override_set with before and after.",
  security: staffSecurity,
  request: {
    params: OverrideParams,
    body: { content: { "application/json": { schema: PlatformOverrideSetRequest } } },
  },
  responses: {
    200: {
      description: "The tenant's effective entitlements after the write",
      content: { "application/json": { schema: PlatformTenantEntitlementsResponse } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Not authenticated"),
    403: problemResponse("Not permitted, or STAFF_SCOPE_DENIED (D-477)"),
    404: problemResponse("Unknown tenant"),
    409: keyReuse,
    422: problemResponse("MODULE_CODE_UNKNOWN, OVERRIDE_INVALID, or a minted key"),
  },
});

registry.registerPath({
  method: "delete",
  path: "/v1/platform/tenants/{id}/overrides/{kind}/{code}",
  operationId: "platformRemoveEntitlementOverride",
  tags: ["platform"],
  summary: "Remove a per-tenant override (platform_admin)",
  description:
    "The plan's value applies again. Audited entitlement.override_removed with the reason and the removed override.",
  security: staffSecurity,
  request: {
    params: OverrideParams,
    body: { content: { "application/json": { schema: PlatformOverrideRemoveRequest } } },
  },
  responses: {
    200: {
      description: "The tenant's effective entitlements after the removal",
      content: { "application/json": { schema: PlatformTenantEntitlementsResponse } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Not authenticated"),
    403: problemResponse("Not permitted, or STAFF_SCOPE_DENIED (D-477)"),
    404: problemResponse("Unknown tenant, or OVERRIDE_NOT_FOUND"),
    409: keyReuse,
    422: mintedKey,
  },
});

// --- usage (spec 15 §2.16, §2.17, S7) ---

registry.registerPath({
  method: "get",
  path: "/v1/platform/tenants/{id}/usage",
  operationId: "platformTenantUsage",
  tags: ["platform"],
  summary: "A tenant's usage: 90 days of daily figures and the month's rollup (all staff roles)",
  description:
    "month is YYYY-MM in UTC and defaults to the current month; a month before the 400-day retention or after the current month answers 400. A day with no reading has a null value, never 0 (spec 15 §9.14).",
  security: staffSecurity,
  request: {
    params: IdParam,
    query: z.object({
      month: z
        .string()
        .regex(/^\d{4}-\d{2}$/)
        .optional(),
    }),
  },
  responses: {
    200: {
      description: "The series and the rollup",
      content: { "application/json": { schema: PlatformTenantUsageResponse } },
    },
    400: problemResponse("Validation failed"),
    401: problemResponse("Not authenticated"),
    403: staffForbidden("Not permitted"),
    404: problemResponse("Unknown tenant"),
  },
});
